import { Loader2Icon } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';

import {
  type PaletteCommand,
  useCommandPaletteContext,
} from './CommandPaletteContext';
import { useEnsureInitialTasksCommand } from './commands';

/**
 * CommandPalette – global command palette triggered with the "\\" key.
 *
 * This is a thin wrapper around the Radix-powered <CommandDialog /> that:
 *   • Listens for the backslash key at the *document* level
 *   • Accepts an array of command descriptors so that any screen/component can
 *     compose its own palette commands without modifying the palette itself
 *   • Shows loading state and visual feedback during command execution
 *   • Automatically filters disabled commands and closes when a command completes
 */
export const CommandPalette: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [executingCommandId, setExecutingCommandId] = useState<string | null>(
    null,
  );

  const { commands } = useCommandPaletteContext();

  // Grab matterId from the current URL (supports /matters/:matterId/*)
  const location = useLocation();
  const matterId = useMemo(() => {
    const match = location.pathname.match(/\/matters\/([0-9a-fA-F-]{36})/);
    return match ? match[1] : undefined;
  }, [location.pathname]);

  // Register commands defined in subfolder
  useEnsureInitialTasksCommand(matterId);

  const availableCommands = useMemo(() => {
    return commands.filter((cmd) => {
      if (typeof cmd.enabled === 'function') return cmd.enabled();
      return cmd.enabled !== false;
    });
  }, [commands]);

  // Key handler – opens palette when "\\" is pressed (and the modal isn't already open)
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Ignore if an input, textarea or contentEditable is focused
    const target = event.target as HTMLElement;
    const isInputElement =
      target?.tagName === 'INPUT' ||
      target?.tagName === 'TEXTAREA' ||
      target?.isContentEditable;

    if (isInputElement) return;

    if (
      event.key === '\\' &&
      !event.shiftKey &&
      !event.ctrlKey &&
      !event.metaKey
    ) {
      event.preventDefault();
      setOpen((prev) => !prev);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleCommandSelect = useCallback(
    async (cmd: PaletteCommand) => {
      // Prevent selecting another command while one is executing
      if (executingCommandId) return;

      setExecutingCommandId(cmd.id);

      try {
        await cmd.action();
      } catch (err) {
        console.error('Command execution failed:', err);
        toast.error(`Failed to execute "${cmd.label}"`);
      } finally {
        setExecutingCommandId(null);
        setOpen(false);
      }
    },
    [executingCommandId],
  );

  // Group commands by group label for display
  const grouped = useMemo(() => {
    const map = new Map<string, PaletteCommand[]>();
    availableCommands.forEach((cmd) => {
      const group = cmd.group ?? 'Commands';
      const existing = map.get(group) ?? [];
      existing.push(cmd);
      map.set(group, existing);
    });
    return Array.from(map.entries());
  }, [availableCommands]);

  const isExecuting = executingCommandId !== null;

  return (
    <CommandDialog open={open} onOpenChange={isExecuting ? undefined : setOpen}>
      <CommandInput
        placeholder="Type a command..."
        disabled={isExecuting}
        className={cn(isExecuting && 'opacity-50')}
      />
      <CommandList>
        <CommandEmpty>No commands available.</CommandEmpty>
        {grouped.map(([groupLabel, cmds]) => (
          <CommandGroup key={groupLabel} heading={groupLabel}>
            {cmds.map((cmd) => {
              const isThisExecuting = executingCommandId === cmd.id;
              const isDisabled = isExecuting && !isThisExecuting;

              return (
                <CommandItem
                  key={cmd.id}
                  value={cmd.label}
                  onSelect={() => handleCommandSelect(cmd)}
                  disabled={isDisabled}
                  className={cn(
                    'flex items-center gap-3 cursor-pointer',
                    isDisabled && 'opacity-50 cursor-not-allowed',
                    isThisExecuting && 'bg-accent',
                  )}
                >
                  {/* Icon or loading spinner */}
                  <span className="flex h-5 w-5 items-center justify-center shrink-0">
                    {isThisExecuting ? (
                      <Loader2Icon className="h-4 w-4 animate-spin text-blue-600 dark:text-blue-400" />
                    ) : (
                      cmd.icon
                    )}
                  </span>

                  {/* Label and description */}
                  <div className="flex flex-col gap-0.5 min-w-0">
                    <span
                      className={cn(
                        'text-sm font-medium truncate',
                        isThisExecuting && 'text-blue-600 dark:text-blue-400',
                      )}
                    >
                      {isThisExecuting ? 'Running...' : cmd.label}
                    </span>
                    {cmd.description && !isThisExecuting && (
                      <span className="text-xs text-muted-foreground truncate">
                        {cmd.description}
                      </span>
                    )}
                  </div>
                </CommandItem>
              );
            })}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
};
